// src/app/components/theme-switcher.component.ts
import { Component, OnInit, OnDestroy, CUSTOM_ELEMENTS_SCHEMA } from '@angular/core';
import { ThemeService, Theme } from './app.theme.service';
import { Subject, takeUntil } from 'rxjs';
import { CommonModule } from '@angular/common';

@Component({
  selector: 'kng-theme',
  template: `
    <sl-button-group hidden>
      <sl-button
        [variant]="currentTheme === 'light' ? 'primary' : 'default'"
        (click)="setTheme('light')"
        size="small">
        <sl-icon name="sun" slot="prefix"></sl-icon>
        Clair
      </sl-button>

      <sl-button
        [variant]="currentTheme === 'auto' ? 'primary' : 'default'"
        (click)="setTheme('auto')"
        size="small">
        <sl-icon name="circle-half" slot="prefix"></sl-icon>
        Auto
      </sl-button>

      <sl-button
        [variant]="currentTheme === 'dark' ? 'primary' : 'default'"
        (click)="setTheme('dark')"
        size="small">
        <sl-icon name="moon" slot="prefix"></sl-icon>
        Sombre
      </sl-button>
    </sl-button-group>


    <!-- Alternative: Simple toggle -->
    <sl-button class="theme-toggle"
      variant="default"
      size="small" (click)="toggleTheme()" circle>
      <sl-icon [name]="icon" label="Settings"></sl-icon>
    </sl-button>

    <div class="zoom">
      <sl-button-group>
        <sl-button
          variant="default"
          size="small" (click)="onZoomPlus()" circle>
          <sl-icon name="plus-lg" label="Agrandir"></sl-icon>
        </sl-button>
        <sl-button
          variant="default"
          size="small" (click)="onZoomMinus()" circle>
          <sl-icon name="dash-lg" label="Réduire"></sl-icon>
        </sl-button>
      </sl-button-group>
      <span
        class="zoom-fader"
        role="meter"
        aria-label="Taille"
        [attr.aria-valuenow]="zoomPercent"
        [attr.aria-valuemin]="zoomMin"
        [attr.aria-valuemax]="zoomMax"
        [style.--zoom-fill]="zoomFill">
      </span>
    </div>
  `,
  standalone: true,
  imports: [CommonModule],
  providers: [ThemeService],
  schemas: [CUSTOM_ELEMENTS_SCHEMA],
  styles: [`
    :host {
      display: block;
      display: flex;
      gap: .5rem;
      align-items: center;
    }
    .theme-toggle {
      --sl-spacing-medium: 0.75rem;
    }
    .zoom {
      display: flex;
      align-items: stretch;
      gap: var(--sl-spacing-2x-small);
      align-items: center;
    }
    .zoom-fader {
      display: flex;
      flex-direction: column;
      justify-content: flex-end;
      align-self: stretch;
      width: 0.25rem;
      -min-height: var(--sl-input-height-medium);
      box-sizing: border-box;
      -border-top: var(--sl-input-border-width) solid var(--sl-input-border-color);
      -border-bottom: var(--sl-input-border-width) solid var(--sl-input-border-color);
      background: var(--sl-color-brand-100);
      pointer-events: none;
    }
    .zoom-fader::after {
      content: '';
      width: 100%;
      height: var(--zoom-fill);
      background: var(--sl-color-orange-500);
    }
  `]
})
export class AppThemeComponent implements OnInit, OnDestroy {
  private destroy$ = new Subject<void>();
  currentTheme: Theme = 'auto';
  zoomPercent = 90;
  zoomFill = '33%';
  readonly zoomMin = Math.round(this.themeService.MIN_ZOOM * 100);
  readonly zoomMax = Math.round(this.themeService.MAX_ZOOM * 100);

  constructor(private themeService: ThemeService) {}

  get icon(): string {
    return this.currentTheme !== 'dark' ? 'moon' : 'sun';
  }

  onZoomPlus(): void {
    this.themeService.zoomIn();
  }

  onZoomMinus(): void {
    this.themeService.zoomOut();
  }

  ngOnInit(): void {
    this.themeService.currentTheme$
      .pipe(takeUntil(this.destroy$))
      .subscribe(theme => {
        this.currentTheme = theme;
      });
    this.themeService.currentZoom$
      .pipe(takeUntil(this.destroy$))
      .subscribe(zoom => {
        this.zoomPercent = Math.round(zoom * 100);
        const span = this.themeService.MAX_ZOOM - this.themeService.MIN_ZOOM;
        this.zoomFill = `${Math.round(((zoom - this.themeService.MIN_ZOOM) / span) * 100)}%`;
      });
  }

  setTheme(theme: Theme): void {
    this.themeService.setTheme(theme);
  }

  toggleTheme(): void {
    this.themeService.toggleTheme();
  }

  ngOnDestroy(): void {
    this.destroy$.next();
    this.destroy$.complete();
  }
}
